<template>
<master-layout pageTitle="Password Settings">
  <div>
    <div v-if="isAdmin">
      <div class="vgrid--no-padding gridMargin">
        <div class="vrow">
          <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-8 vcol-md-8 vcol-sm-12 vcol-xs-12 model-sections">
            <div class="previewHeader">
              <p class="vtext-card-title--light vmb-10">Expiry settings</p>
              <p class="vtext-subtitle">Manage settings related to password reset for all team member accounts</p>
            </div>
            <div class="sectionPadding">
              <div class="enableExpiry vmb-20">
                <VSwitch :checked="switchSetupModeValue" @change="onToggleChange" connotation="cta" label="Enable password expiry setting" reverse class="switchFullWidth"></VSwitch>
              </div>
              <div class="vmb-20">
                <p class="vtext-subtitle vpb-10">Passwords expire in</p>
                <div class="post_text_cont">
                  <InputNumber :disabled="!switchSetupModeValue" v-model="trigger_days" placeholder="Eg.30" ariaLabel="password expire" class="sctn-input-discount" :min="1" :max="180"> </InputNumber>
                  <span class="post_text"> day(s)</span>
                </div>
              </div>
              <div class="info-box cust-brdr vmb-20">
                <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                <p class="vtext-body">Enter a value in days. Max value = 180</p>
              </div>
              <VButton
                class="vvd-btn-primary vvd-btn-block"
                connotation="cta"
                appearance="filled"
                :label="busySave ? 'Saving...' : 'Save'"
                :disabled="!switchSetupModeValue || busySave || !trigger_days"
                @click="addPasswordSetup"
              ></VButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <page-access-not-allowed></page-access-not-allowed>
    </div>
  </div>

  <!-- Confirmation Dialog -->
  <VDialog
    :open="switchSetupMode"
    headline="Are you sure"
    modal
    @close="onDialogClose"
  >
    <template #body>
      <p class="vtext-body">You want to {{ switchSetupModeValue ? 'enable' : 'disable' }} the password expiry setting?</p>
    </template>
    <template #action-items>
      <VButton
        class="vvd-btn-cancel"
        appearance="filled"
        label="Cancel"
        @click="cancelSwitch"
      ></VButton>
      <VButton
        class="vvd-btn-primary"
        connotation="cta"
        appearance="filled"
        label="Yes"
        @click="confirmSwitch"
      ></VButton>
    </template>
  </VDialog>
</master-layout>
</template>

<script>
import InputNumber from "@/components/inputNumber.vue";
import apiInterceptor from '@/shared/apiInterceptor';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { environment } from '@/shared/env';

export default {
  components: {
    InputNumber,
  },
  data() {
    return {
      trigger_days: undefined,
      switchSetupMode: false,
      switchSetupModeValue: false,
      busySave: false,
      initialized: false,
    }
  },
  computed: {
    isAdmin() { return useAuthStore().isAdmin; },
  },
  mounted(){
    if(this.isAdmin){
      this.initView();
    } else {
      useUIStore().updateToast({
        open: true,
        message: "Unauthorized to use this page!",
        type: "danger"
      })
      this.$router.replace('/');
    }
  },
  methods: {
    onToggleChange(event) {
      if (!this.initialized) return;
      this.switchSetupModeValue = event.target.checked;
      this.switchSetupMode = true;
    },
    onDialogClose() {
      this.switchSetupMode = false;
    },
    cancelSwitch() {
      this.switchSetupMode = false;
      this.switchSetupModeValue = !this.switchSetupModeValue;
    },
    async initView() {
      try {
        useUIStore().updateLoading({ isLoading: true, message: "Loading..." });
        const setupResponse = await apiInterceptor.get( environment("VUE_APP_API_URL") + "settings/v1/password-expiry/" );
        this.switchSetupModeValue = setupResponse.active;
        this.trigger_days = parseInt(setupResponse.trigger_days || 0);
        this.$nextTick(() => { this.initialized = true; });
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Oops! Something went wrong",
          type: "danger"
        })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async confirmSwitch() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        var reqBody = {
          enable: this.switchSetupModeValue
        }
        await apiInterceptor.patch( environment("VUE_APP_API_URL") + "settings/v1/password-expiry/", reqBody );
        useUIStore().updateToast({
          open: true,
          message: (this.switchSetupModeValue ? "Enabled" : "Disabled") + " successfully!",
          type: "success"
        });
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        this.switchSetupMode = false;
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async addPasswordSetup() {
      try {
        if(this.trigger_days < 0){
          useUIStore().updateToast({ open: true, message: "please enter valid number and try again", type: "danger" })
          return
        } else if(this.trigger_days > 180){
          useUIStore().updateToast({ open: true, message: "Sorry! You can't set more than 180 days for password expiry.", type: "danger" })
          return
        }
        this.busySave = true;
        let reqBody = {
          days: this.trigger_days
        }
        await apiInterceptor.post( environment("VUE_APP_API_URL") + "settings/v1/password-expiry/", reqBody );
        useUIStore().updateToast({
          open: true,
          message: "Settings saved successfully!",
          type: "success"
        });        
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        this.busySave = false;
      }
    }
  }
}
</script>
<style lang="scss" scoped src="../../resources/css/settings.scss"> </style>
<style scoped>
.sectionPadding {
  padding: 10px 20px 20px 20px;
}

.enableExpiry {
  padding: 10px 0;
}

.switchFullWidth {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
</style>